import {ChangeDetectorRef, Component} from '@angular/core';
import {
  BoothApproval,
  BoothBuildState,
  BoothPurchase,
  BoothPurchaseState,
  Dated,
  EventBooth
} from "../../entities/booths/EventBooth";
import {APIService} from "../../services/APIService";
import {ActivatedRoute} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {EditDialogueComponent} from "./edit-dialogue/edit-dialogue.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormControl} from "@angular/forms";
import {BasicEventPlayer} from "../../entities/auth/BasicEventPlayer";
import {BoothInfo, BoothSocial} from "../../entities/booths/BoothInfo";
import {ChangeStateComponent, ChangeStateData} from "./change-state/change-state.component";

@Component({
  selector: 'app-booth-view',
  templateUrl: './booth-view.component.html',
  styleUrls: ['./booth-view.component.css']
})
export class BoothViewComponent {
  booth: EventBooth | null = null;
  boothInfo: BoothInfo | null = null;
  socials: BoothSocial[] = [];
  purchaseHistory: Dated<BoothPurchase>[] = [];
  approvalHistory: Dated<BoothApproval>[] = [];

  columns: string[] = ['player', 'actions']

  hasSocials: boolean = false;

  nameControl: FormControl = new FormControl('');

  constructor(private apiService: APIService,
              private route: ActivatedRoute,
              private dialog: MatDialog,
              private snack: MatSnackBar,
              private changeDetection: ChangeDetectorRef) {
    this.update();
  }

  edit() {
    this.dialog.open(EditDialogueComponent, {data: this.booth}).afterClosed().subscribe((data) => {
      const name: string = data['name'];
      const type: string = data['type'];
      const owner: string = data['owner'];
      this.apiService.editBooth(this.booth!.id, name, type, owner).then((a) => {
        if (a.success) {
          this.snack.open("Success!")
          this.update();
        }
      })
    });
  }

  openState(type: "purchase" | "approval", defaultState?: string) {
    const data: ChangeStateData = {type: type, default: defaultState, booth: this.booth!}
    this.dialog.open(ChangeStateComponent, {data: data}).afterClosed().subscribe((raw) => {
      const data = raw as { comment?: string; state?: string };
      if (data.state === undefined)
        return;

      const state = type === "purchase" ? BoothPurchaseState[data.state as keyof typeof BoothPurchaseState]
        : BoothBuildState[data.state as keyof typeof BoothBuildState];

      if (type === "purchase") {
        this.apiService.changePurchaseState(parseInt(this.booth!.id), state as BoothPurchaseState,
          data.comment).then((r) => {
          this.update();
        })
      }
    })
  }

  parseLink(name: string, value: string) {
    if (value.startsWith("https://") || value.startsWith("http://")) {
      return value;
    }

    const starts = ["twitter.com", "x.com", "discord.gg", "facebook.com", "twitch.tv", "instagram.com", "youtube.com", "tiktok.com"];
    for (const start of starts) {
      if (value.startsWith(start)) {
        value = "https://" + value;
        return value;
      }
    }

    if (name === "website" && !value.startsWith("http")) {
      value = "https://" + value;
      return value;
    }

    if (value.startsWith("@"))
      value = value.substring(1);
    if (name === "twitter" || name === "x") {
      return "https://x.com/" + value;
    } else if (name === "youtube") {
      return "https://youtube.com/" + value;
    } else if (name === "twitch") {
      return "https://twitch.tv/" + value;
    } else if (name === "discord") {
      return "https://twitch.tv/" + value;
    } else if (name === "tiktok") {
      return "https://tiktok.com/@" + value;
    }

    return value;
  }

  update() {
    const id = this.route.snapshot.params['id'];
    this.apiService.getBooth(id).then((a) => this.booth = a);
    this.apiService.getBoothInfo(id).then((a) => {
      this.boothInfo = a
      this.hasSocials = Object.keys(this.boothInfo.socials).length > 0;
      this.socials = [];
      for (const key of Object.keys(this.boothInfo.socials))
        this.socials.push({name: key, value: this.boothInfo.socials[key]});
    });
    this.apiService.getBoothHistory(id).then((history) => {
      this.purchaseHistory.splice(0);
      this.approvalHistory.splice(0);

      const purchaseChanges = history.purchaseChanges.map((a) => ({
        change: a,
        time: new Date(Number(a.time)).toLocaleString()
      }));
      const approvalChanges = history.approvalChanges.map((a) => ({
        change: a,
        time: new Date(Number(a.time)).toLocaleString()
      }));

      for (const purchase of purchaseChanges)
        this.purchaseHistory.push(purchase);
      for (const approval of approvalChanges)
        this.approvalHistory.push(approval);
    })
  }

  add(player: BasicEventPlayer) {
    this.apiService.addMember(this.booth!.id, player.username)
      .then((response) => {
        if (response.success) {
          this.snack.open("Added player " + player.username)
          this.apiService.getBooth(this.booth!.id).then((a) => this.booth = a);
        }
      })
  }

  remove(player: BasicEventPlayer) {
    this.apiService.removeMember(this.booth!.id, player.username)
      .then((response) => {
        if (response.success) {
          this.snack.open("Removed player " + player.username)
          this.apiService.getBooth(this.booth!.id).then((a) => this.booth = a);
        }
      })
  }

  sendOpenedEmail() {
    if (!this.booth) return;
    this.apiService.sendBoothEmail(parseInt(this.booth.id), 'booth_accepted_opened')
      .then(() => this.snack.open('Sent "Booth Opened" email'))
      .catch(() => this.snack.open('Failed to send email'))
  }

  protected readonly BoothPurchaseState = BoothPurchaseState;
}
